import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Select } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { get } from "lodash";
import moment from "moment";

import { setSuccessMessage } from "app/global/slice";
import { canCreatePermission } from "configs/constants";
import { doExportCsv } from "helper/export";
import { allowPermission, fullName } from "helper/util";
import { selectRole } from "modules/auth/store/authSlice";
import { buildFirstLevelFilter } from "modules/client/internal-drivers/helper";
import { selectAllInternalDrivers } from "modules/client/internal-drivers/store/slice";
import { getInternalDriverList } from "modules/client/internal-drivers/store/thunks";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import { buildFirstLevelFilterRoute } from "modules/shared/PickupDropoff/helper";
import { isEmpty } from "lodash";
import invoicingApi from "modules/client/invoicing/store/api";
import { shortId } from "helper/util";
import { INVOICE_TYPES } from "configs/constants";
import paginationFunc from "../../../../helper/hooks/pagination";
import ImportLocations from "../components/exports";
import FilterRoute from "../components/header/FilterRoute";
import RoutesHeader from "../components/header/RoutesHeader";
import RouteListTable from "../components/tables/RouteListTable";
import WaitingModal from "../components/WaitingModal";
import {
  selectAllRoutes,
  selectFilter,
  selectMetadata,
  updateFilterRoutes,
  updatePaginationRoutes
} from "../store/slice";
import {
  assignDriver,
  cancelRoute,
  doDeleteRoute,
  getRouteList,
  reassignDriver
} from "../store/thunks";
import { setGlobalLoading } from "app/global/slice";

const RouteListPage = () => {
  const fileInputRef = useRef();
  const [totalStop, setTotalStop] = useState(0);
  const [currentStop, setCurrentStop] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const [currentRoute, setCurrentRoute] = useState({});
  const [assignedDriverId, setAssignedDriverId] = useState("");
  const isLoading = useSelector((state) => state.global.isLoading);
  const breadCrumbs = [{ text: "ROUTES", url: "/" }];
  const dispatch = useDispatch();
  const orderList = useSelector(selectAllRoutes);
  const metadata = useSelector(selectMetadata);
  const filter = useSelector(selectFilter);
  const driverList = useSelector(selectAllInternalDrivers);
  const pagination = paginationFunc(
    { ...metadata, itemsPerPage: get(metadata, "itemsPerPage", 20) },
    "Active Job",
    updatePaginationRoutes,
    dispatch
  );
  const page = metadata?.currentPage || 1;
  const pageSize = metadata?.itemsPerPage || 20;
  const canCreate = allowPermission(useSelector(selectRole), canCreatePermission);

  const percent = useMemo(
    () => Math.round((currentStop * 100) / totalStop),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStop]
  );

  useEffect(() => {
    dispatch(getPackageTypeList());
    dispatch(
      getInternalDriverList({
        pageSize: 1000,
        filter: buildFirstLevelFilter({ is_internal_driver: true, active: true })
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { pickupDateRange = [] } = filter || {};
    dispatch(
      getRouteList({
        page: page,
        pageSize: pageSize,
        filter: buildFirstLevelFilterRoute(
          pickupDateRange?.length === 2
            ? {
                ...filter,
                startPickupDate: pickupDateRange[0],
                endPickupDate: pickupDateRange[1]
              }
            : filter
        ),
        pickupFilter: filter
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filter]);

  const handleClickFilter = (values) => {
    dispatch(updateFilterRoutes(values));
  };

  const handleDeleteOrder = (id) => {
    dispatch(doDeleteRoute(id));
  };

  const handleCancelRoute = async (routeId) => {
    try {
      const { error } = await dispatch(cancelRoute(routeId));
      if (!error) {
        dispatch(setSuccessMessage("Canceled job successfully!"));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleExportRoutes = async () => {
    try {
      const { pickupDateRange = [] } = filter || {};
      setExporting(true);
      await doExportCsv({
        path: "routes/export",
        name: `Active-Jobs-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterRoute(
            pickupDateRange?.length === 2
              ? {
                  ...filter,
                  startPickupDate: pickupDateRange[0],
                  endPickupDate: pickupDateRange[1]
                }
              : filter
          ),
          pickupFilter: filter
        }
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportOldInvoices = async () => {
    try {
      const { pickupDateRange = [], status } = filter || {};
      const filterStatuses = isEmpty(status) ? ["canceled", "completed"] : status;

      setExporting(true);
      await doExportCsv({
        path: "routes/export-invoicing",
        name: `invoicing-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterRoute(
            pickupDateRange?.length === 2
              ? {
                  ...filter,
                  status: filterStatuses,
                  startPickupDate: pickupDateRange[0],
                  endPickupDate: pickupDateRange[1]
                }
              : { ...filter, status: filterStatuses }
          ),
          // Client, driver, service and factura selections live here, not in `filter`
          pickupFilter: { ...filter, status: filterStatuses }
        }
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportInvoicePdf = async (routeId) => {
    try {
      const routeData = orderList.find((route) => route.id === routeId);
      const clientName = routeData?.client?.clientName || "Client";
      const serviceDate = routeData?.startDate || "Unknown-Date";
      const invoiceId = shortId(routeId);

      const customFilename = `${clientName}_${invoiceId}_${serviceDate}.pdf`.replace(
        /[^\w\s.-]/gi,
        ""
      );

      const response = await invoicingApi.exportInvoicePdf(
        routeId,
        INVOICE_TYPES.CLIENT_INVOICE
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = customFilename;

      // Add link to DOM, click it, then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.log(err);
      dispatch(setSuccessMessage("Failed to export invoice PDF"));
    }
  };
  const handleClickImport = () => {
    fileInputRef.current.click();
  };

  const handleOnChangeImport = (e) => {
    console.log("e", e.target.files[0]);
  };

  const handleCloseModalImport = () => {
    setShowImport(false);
    setShowWaitingModal(true);
  };

  const handleAssignDriverClick = (route) => {
    setCurrentRoute(route);
    setAssignedDriverId(route?.driverId);
    setShowAssignDriver(true);
  };

  const handleSubmitAssignDriver = async () => {
    if (!assignedDriverId) return;
    try {
      const { error } = await dispatch(
        currentRoute?.driverId
          ? reassignDriver({ routeId: currentRoute?.id, driverId: assignedDriverId })
          : assignDriver({ routeId: currentRoute?.id, driverId: assignedDriverId })
      );
      if (!error) {
        dispatch(
          setSuccessMessage(
            `${currentRoute?.driverId ? "Reassigned" : "Assigned"} successfully!`
          )
        );
        setShowAssignDriver(false);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="client-order-list-page">
      <RoutesHeader
        breadCrumbs={breadCrumbs}
        showFilter={showFilter}
        showCreateButton={canCreate}
        setShowFilter={setShowFilter}
        setShowImport={setShowImport}
        fileInputRef={fileInputRef}
        handleClickImport={handleClickImport}
        handleOnChangeImport={handleOnChangeImport}
        onExport={handleExportRoutes}
        onExportOldInvoices={handleExportOldInvoices}
        exporting={exporting}
      />
      {showFilter && (
        <FilterRoute
          defaultValues={filter}
          setShowFilter={setShowFilter}
          handleClickFilter={handleClickFilter}
          onReset={() =>
            dispatch(
              updateFilterRoutes({
                status: ["pending", "accepted", "in-progress"],
                pickupDateRange: [
                  moment().subtract(1, "month").startOf("month"),
                  moment().endOf("month")
                ]
              })
            )
          }
        />
      )}
      <div className="content-table">
        <RouteListTable
          doDelete={handleDeleteOrder}
          onCancelRoute={handleCancelRoute}
          onAssignDriverClick={handleAssignDriverClick}
          onReassignDriverClick={handleAssignDriverClick}
          dataSource={orderList}
          pagination={{ ...pagination, size: "small" }}
          hiddenColumns={["Min. Vehicle Type"]}
          onExportInvoicePdf={handleExportInvoicePdf}
        />
      </div>
      {showImport && (
        <Modal
          closable={false}
          visible={true}
          className="no-padding-modal import-modal"
          onCancel={() => {
            setShowImport(false);
            dispatch(setGlobalLoading(false));
          }}
          footer={false}
          width={1000}
        >
          <ImportLocations
            handleCloseModal={handleCloseModalImport}
            setCurrentStop={setCurrentStop}
            setTotalStop={setTotalStop}
          />
        </Modal>
      )}
      {showWaitingModal && (
        <Modal
          closable={false}
          visible={true}
          className="no-padding-modal"
          onCancel={() => setShowWaitingModal(false)}
          footer={false}
          width={400}
        >
          <WaitingModal percent={percent} />
        </Modal>
      )}
      {showAssignDriver && (
        <Modal
          closable={false}
          visible={true}
          title={`${currentRoute?.driverId ? "Reassign" : "Assign"} Driver`}
          width={600}
          footer={
            <div className="buttons-modal">
              <Button size="large" onClick={() => setShowAssignDriver(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  !assignedDriverId || assignedDriverId === currentRoute?.driverId
                }
                loading={isLoading}
                size="large"
                type="primary"
                onClick={handleSubmitAssignDriver}
              >
                Assign
                <CheckCircleOutlined />
              </Button>
            </div>
          }
        >
          <Select
            size="large"
            placeholder="Select driver"
            onChange={(driverId) => setAssignedDriverId(driverId)}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="children"
            value={assignedDriverId}
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {driverList.map((driver) => (
              <Select.Option key={driver.id} value={driver.id}>
                {fullName(driver)}
              </Select.Option>
            ))}
          </Select>
        </Modal>
      )}
    </div>
  );
};

export default RouteListPage;
